(() => {
  "use strict";

  window.zag = window.zag || {};

  window.zag.components.ZagFeaturedProduct = class extends (
    window.zag.components.ZagComponent
  ) {
    constructor() {
      super();

      this.addToCartVisible = false;
      this.footerVisible = false;
      this.mountingVideoPlaying = false;
      this.zoomThrottleId = null;
    }

    connectedCallback() {
      if (!this.data) {
        console.error("Featured product data not found");
        return;
      }

      this.initializeStickyBar();
      this.attachEventListeners();
      this.initializeState();
    }

    // =========================================
    // Data & DOM Getters
    // =========================================

    get data() {
      return this.cache.get("data", "productData", () => {
        const dataScript = this.querySelector(`#${this.section.id}--data`);
        return dataScript ? JSON.parse(dataScript.textContent) : null;
      });
    }

    get optionValues() {
      return this.cache.get("dom", "optionValues", () =>
        Array.from(this.querySelectorAll(".option-value"))
      );
    }

    get skinCards() {
      return this.cache.get("dom", "skinCards", () =>
        Array.from(this.querySelectorAll(".skin-card"))
      );
    }

    get bindingCards() {
      return this.cache.get("dom", "bindingCards", () =>
        Array.from(this.querySelectorAll(".binding-card"))
      );
    }

    get cramponCards() {
      return this.cache.get("dom", "cramponCards", () =>
        Array.from(this.querySelectorAll(".crampon-card"))
      );
    }

    get mediaItems() {
      return this.cache.get("dom", "mediaItems", () =>
        Array.from(this.querySelectorAll(".media-item"))
      );
    }

    get mainAddToCartButton() {
      return this.cache.get("dom", "mainAddToCartButton", () =>
        this.querySelector(".zag-add-to-cart")
      );
    }

    get addToCartButtons() {
      return this.cache.get("dom", "addToCartButtons", () =>
        Array.from(this.querySelectorAll("zag-add-to-cart"))
      );
    }

    get prices() {
      return this.cache.get("dom", "prices", () =>
        Array.from(this.querySelectorAll("zag-price"))
      );
    }

    get moreOptionsButton() {
      return this.cache.get("dom", "moreOptionsButton", () =>
        this.querySelector(".more-options-button")
      );
    }

    get bundlePopinContainer() {
      return this.cache.get("dom", "bundlePopinContainer", () =>
        this.querySelector(".bundle-popin-container")
      );
    }

    get bundlePopin() {
      return this.cache.get("dom", "bundlePopin", () =>
        this.querySelector(".bundle-popin")
      );
    }

    get closePopinButton() {
      return this.cache.get("dom", "closePopinButton", () =>
        this.querySelector(".close-bundle-popin")
      );
    }

    get previousBundlePopinStepButton() {
      return this.cache.get("dom", "previousBundlePopinStepButton", () =>
        this.querySelectorAll(".previous-bundle-popin-step")
      );
    }

    get nextBundlePopinStepButton() {
      return this.cache.get("dom", "nextBundlePopinStepButton", () =>
        this.querySelector(".next-bundle-popin-step")
      );
    }

    get noMountingButton() {
      return this.cache.get("dom", "noMountingButton", () =>
        this.querySelector(".no-mounting-configuration")
      );
    }

    get finishBundleButton() {
      return this.cache.get("dom", "finishBundleButton", () =>
        this.querySelector(".finish-bundle-configuration")
      );
    }

    get customerLevelSelect() {
      return this.cache.get(
        "dom",
        "customerLevelSelect",
        () =>
          this.querySelector(
            "#shopify-section-" +
            this.dataset.sectionId +
            "--customer-level-select"
          ) || this.querySelector("[id*='customer-level-select']")
      );
    }

    get customerLevelLabel() {
      return this.cache.get("dom", "customerLevelLabel", () =>
        this.customerLevelSelect?.parentElement.querySelector("label")
      );
    }

    get customerAgeSelect() {
      return this.cache.get(
        "dom",
        "customerAgeSelect",
        () =>
          this.querySelector(
            "#shopify-section-" +
            this.dataset.sectionId +
            "--customer-age-select"
          ) || this.querySelector("[id*='customer-age-select']")
      );
    }

    get customerAgeLabel() {
      return this.cache.get("dom", "customerAgeLabel", () =>
        this.customerAgeSelect?.parentElement.querySelector("label")
      );
    }

    get customerHeightInput() {
      return this.cache.get("dom", "customerHeightInput", () =>
        this.querySelector(".customer-height-input")
      );
    }

    get customerHeightLabel() {
      return this.cache.get("dom", "customerHeightLabel", () =>
        this.customerHeightInput?.parentElement.querySelector("label")
      );
    }

    get bootLengthInput() {
      return this.cache.get("dom", "bootLengthInput", () =>
        this.querySelector(".boot-length-input")
      );
    }

    get bootLengthLabel() {
      return this.cache.get("dom", "bootLengthLabel", () => {
        return this.querySelector(".boot-length-label");
      });
    }

    get customerWeightInput() {
      return this.cache.get("dom", "customerWeightInput", () =>
        this.querySelector(".customer-weight-input")
      );
    }

    get customerWeightLabel() {
      return this.cache.get("dom", "customerWeightLabel", () => {
        return this.querySelector(".customer-weight-label");
      });
    }

    get removeItemButtons() {
      return this.cache.get("dom", "removeItemButtons", () =>
        Array.from(this.querySelectorAll(".remove-item-button"))
      );
    }

    get changeItemButtons() {
      return this.cache.get("dom", "changeItemButtons", () =>
        Array.from(this.querySelectorAll(".change-item-button"))
      );
    }

    get stickyBar() {
      return this.cache.get("dom", "stickyBar", () =>
        this.querySelector(".sticky-bar")
      );
    }

    get locale() {
      return this.cache.get("shop", "locale", () => {
        return window.Shopify?.locale || "fr";
      });
    }

    get country() {
      return this.cache.get("shop", "country", () => {
        return window.Shopify?.country || "FR";
      });
    }

    get currency() {
      return this.cache.get("shop", "currency", () => {
        return window.Shopify?.currency?.active || "EUR";
      });
    }

    get mountingVideos() {
      return this.cache.get("dom", "mountingVideos", () => {
        return Array.from(this.querySelectorAll(".mounting-video"));
      });
    }

    get mountingVideoButton() {
      return this.cache.get("dom", "mountingVideoButton", () => {
        return this.querySelector(
          ".mounting-video-container .play-pause-button"
        );
      });
    }

    get mountingInfoCheckbox() {
      return this.cache.get("dom", "mountingInfoCheckbox", () =>
        this.querySelector(".mounting-info-checkbox")
      );
    }

    get mountingInfoError() {
      return this.cache.get("dom", "mountingInfoError", () =>
        this.querySelector(".mounting-info-error")
      );
    }

    get galleryVideoContainers() {
      return this.cache.get("dom", "galleryVideoContainers", () => {
        return Array.from(this.querySelectorAll(".media-item video"));
      });
    }

    get externalVideoContainers() {
      return this.cache.get("dom", "externalVideoContainers", () => {
        return Array.from(this.querySelectorAll(".media-item iframe"));
      });
    }

    get gallerySlider() {
      return this.cache.get("dom", "gallerySlider", () =>
        this.querySelector(".gallery-container .media-list")
      );
    }

    get galleryVideos() {
      return this.cache.get("dom", "galleryVideos", () =>
        Array.from(this.querySelectorAll(".media-item video"))
      );
    }

    get galleryExternalVideos() {
      return this.cache.get("dom", "galleryExternalVideos", () =>
        Array.from(this.querySelectorAll("iframe.media-item"))
      );
    }

    get galleryPlayPauseButtons() {
      return this.cache.get("dom", "galleryPlayPauseButtons", () =>
        Array.from(
          this.querySelectorAll(".media-item .play-pause-button__button")
        )
      );
    }

    // =========================================
    // Initialization
    // =========================================

    initializeStickyBar() {
      this.observers.intersection(
        this.mainAddToCartButton,
        this.handleMainAddToCartIntersection,
        { threshold: 0.25 }
      );

      if (window.zag.dom.footer) {
        this.observers.intersection(
          window.zag.dom.footer,
          this.handleFooterIntersection,
          { threshold: 0.05 }
        );
      }
    }

    attachEventListeners() {
      this.optionValues.forEach((button) => {
        this.events.add(button, "click", this.handleOptionValueClick);
      });

      if (this.data.is_bundle_context) {
        this.skinCards.forEach((card) => {
          this.events.add(card, "click", this.handleSkinCardClick);
        });

        this.bindingCards.forEach((card) => {
          this.events.add(card, "click", this.handleBindingCardClick);

          card.querySelectorAll(".binding-option-value").forEach((button) => {
            this.events.add(
              button,
              "click",
              this.handleBindingOptionValueClick
            );
          });

          const toggle = card.querySelector(".option-description-toggle");
          if (toggle) {
            this.events.add(toggle, "click", this.handleDescriptionToggle);
          }
        });

        this.cramponCards.forEach((card) => {
          this.events.add(card, "click", this.handleCramponCardClick);
        });

        if (this.moreOptionsButton) {
          this.events.add(
            this.moreOptionsButton,
            "click",
            this.openBundlePopin
          );
        }

        if (this.closePopinButton) {
          this.events.add(
            this.closePopinButton,
            "click",
            this.closeBundlePopin
          );
        }

        if (this.bundlePopinContainer) {
          this.events.add(
            this.bundlePopinContainer,
            "click",
            this.handlePopinBackgroundClick
          );
        }

        this.previousBundlePopinStepButton.forEach((button) => {
          this.events.add(button, "click", this.handlePreviousPopinStep);
        });

        if (this.nextBundlePopinStepButton) {
          this.events.add(
            this.nextBundlePopinStepButton,
            "click",
            this.handleNextPopinStep
          );
        }

        if (this.noMountingButton) {
          this.events.add(
            this.noMountingButton,
            "click",
            this.closeBundlePopin
          );
        }

        if (this.finishBundleButton) {
          this.events.add(
            this.finishBundleButton,
            "click",
            this.handleFinishBundleConfiguration
          );
        }

        if (this.bootLengthInput) {
          this.events.add(
            this.bootLengthInput,
            "input",
            this.validateBootLength
          );
        }

        if (this.customerHeightInput) {
          this.events.add(
            this.customerHeightInput,
            "input",
            this.validateHeight
          );
        }

        if (this.customerWeightInput) {
          this.events.add(
            this.customerWeightInput,
            "input",
            this.validateWeight
          );
        }

        this.removeItemButtons.forEach((button) => {
          this.events.add(button, "click", this.handleRemoveItemClick);
        });

        this.changeItemButtons.forEach((button) => {
          this.events.add(button, "click", this.handleChangeItemClick);
        });

        if (this.mountingVideos.length > 0) {
          this.events.add(
            this.mountingVideoButton,
            "click",
            this.onMountingVideoButtonClick
          );
        }
      }

      if (this.gallerySlider) {
        this.events.add(
          this.gallerySlider,
          "zag-slider:change",
          this.handleGallerySlideChange
        );
      }

      this.galleryPlayPauseButtons.forEach((button) => {
        this.events.add(button, "click", this.handleGalleryVideoToggle);
      });

      this.mediaItems.forEach((mediaItem) => {
        this.events.add(mediaItem, "pointermove", this.handleMediaItemHover);
        this.events.add(mediaItem, "pointerleave", this.handleMediaItemLeave);
      });

      this.events.add(window, "resize", this.pauseAllGalleryVideos);

      this.addToCartButtons.forEach((button) => {
        this.events.add(button, "click", this.handleAddToCart);
      });
    }

    initializeState() {
      this.updateUI();
      this.updatePrice();
    }

    // =========================================
    // Sticky Bar Management
    // =========================================

    handleMainAddToCartIntersection = (entries) => {
      entries.forEach((entry) => {
        this.addToCartVisible = entry.isIntersecting;
        this.updateStickyBarVisibility();
      });
    };

    handleFooterIntersection = (entries) => {
      entries.forEach((entry) => {
        this.footerVisible = entry.isIntersecting;
        this.updateStickyBarVisibility();
      });
    };

    updateStickyBarVisibility() {
      const shouldShow = !this.addToCartVisible && !this.footerVisible;
      this.modifiers.set("sticky-bar", shouldShow);
    }

    // =========================================
    // Selection Management
    // =========================================

    updateSelection(type, variantId, options = {}) {
      const selectionKey = `${type}_variant`;
      const previousId = this.data.selection[selectionKey]?.id;

      if (type === "featured") {
        if (variantId === null) return;
        this.data.selection[selectionKey].id = variantId;
      } else if (type === "mounting") {
        this.data.selection[selectionKey].id = variantId;
        if (options.configuration) {
          this.data.selection.mounting_configuration = options.configuration;
        }
      } else {
        if (variantId === null) {
          this.data.selection[selectionKey].id = null;
        } else if (previousId === variantId) {
          this.data.selection[selectionKey].id = null;
          variantId = null;
        } else {
          this.data.selection[selectionKey].id = variantId;
        }
      }

      this.processSideEffects(type, previousId, variantId);

      this.updateUI();
      this.updatePrice();
    }

    processSideEffects(type, previousId, newId) {
      switch (type) {
        case "featured":
          this.handleFeaturedChange(previousId, newId);
          break;
        case "binding":
          this.handleBindingChange(previousId, newId);
          break;
      }

      if (type === "featured" && this.data.is_bundle_context) {
        const currentVariant = this.getCurrentVariant("featured");
        if (currentVariant && !currentVariant.available) {
          this.updateSelection("skin", null);
          this.updateSelection("binding", null);
          this.updateSelection("crampon", null);
        }
      }
    }

    handleFeaturedChange(previousId, newId) {
      if (!newId) return;

      const prevVariant = previousId
        ? this.data.featured_product.variants[previousId]
        : null;
      const newVariant = this.data.featured_product.variants[newId];

      const sizeOptionName = this.data.option_names.size_option;
      const colorOptionName = this.data.option_names.color_option;

      if (
        prevVariant?.options[sizeOptionName] !==
        newVariant.options[sizeOptionName]
      ) {
        this.validateSkinSelection(newVariant.options[sizeOptionName]);
      }

      if (
        prevVariant?.options[colorOptionName] !==
        newVariant.options[colorOptionName]
      ) {
        this.updateBindingGalleries();
      }

      this.navigateToVariantMedia(newVariant);
    }

    handleBindingChange(previousId, newId) {
      this.updateCramponVisibility(newId);

      const currentCramponId = this.data.selection.crampon_variant?.id;

      if (currentCramponId) {
        if (!newId) {
          this.data.selection.crampon_variant.id = null;
        } else {
          const bindingProduct = Object.values(
            this.data.bundle_data.binding_products
          ).find((product) =>
            Object.values(product.variant_lookup).includes(newId)
          );

          if (bindingProduct) {
            const compatibleCramponVariants =
              bindingProduct.compatible_crampon_variants || [];
            const cramponVariant =
              this.data.bundle_data.crampon_variants[currentCramponId];

            if (
              !compatibleCramponVariants.includes(cramponVariant) ||
              !cramponVariant.available
            ) {
              this.data.selection.crampon_variant.id = null;
            }
          }
        }
      }

      if (!newId) {
        if (this.data.selection.mounting_variant?.id) {
          this.updateSelection("mounting", null);
        }
        if (this.data.selection.mounting_configuration) {
          this.data.selection.mounting_configuration = {
            boot_length: null,
            customer_weight: null,
          };
        }
      }

      if (newId) {
        this.updateBindingGalleries();
      }
    }

    validateSkinSelection(newSize) {
      const currentSkinId = this.data.selection.skin_variant?.id;
      if (!currentSkinId) return;

      const sizeOptionName = this.data.option_names.size_option;

      const currentSkinVariant =
        this.data.bundle_data.skin_variants[currentSkinId];

      if (
        currentSkinVariant &&
        !this.skinSizeMatches(
          currentSkinVariant.options[sizeOptionName],
          newSize
        )
      ) {
        const currentSkinProduct =
          this.data.bundle_data.skin_products[currentSkinVariant.product_id];
        const newSkinVariant = this.findSkinVariantForSize(
          currentSkinProduct,
          newSize
        );

        if (newSkinVariant && newSkinVariant?.available) {
          this.data.selection.skin_variant.id = newSkinVariant.id;
        } else {
          this.data.selection.skin_variant.id = null;
          this.showSkinSizeError();
        }
      }
    }

    // =========================================
    // Variant Validation
    // =========================================

    /**
     * Validates variant selection and handles fallback when target variant doesn't exist
     * @param {string} optionName - The name of the option being changed
     * @param {string} optionValue - The new value for the option
     * @returns {number|null} - The validated variant ID or null if no valid variant found
     */
    validateAndSelectVariant(optionName, optionValue) {
      const currentVariant = this.getCurrentVariant("featured");
      if (!currentVariant) return null;

      const targetOptions = {
        ...currentVariant.options,
        [optionName]: optionValue,
      };

      const targetVariantKey = this.buildVariantKey(targetOptions);
      const targetVariantId =
        this.data.featured_product.variant_lookup[targetVariantKey];

      if (targetVariantId) {
        return targetVariantId;
      }

      const fallbackVariantId = this.findFirstAvailableVariant(
        optionName,
        optionValue
      );

      if (fallbackVariantId) {
        const fallbackVariant =
          this.data.featured_product.variants[fallbackVariantId];
        this.data.selection.selected_options = { ...fallbackVariant.options };
      }

      return fallbackVariantId;
    }

    /**
     * Finds the first available variant for a given option name and value
     * @param {string} optionName - The name of the option
     * @param {string} optionValue - The value of the option
     * @returns {number|null} - The first available variant ID or null
     */
    findFirstAvailableVariant(optionName, optionValue) {
      const variants = Object.values(this.data.featured_product.variants);

      const matchingVariants = variants.filter(
        (variant) => variant.options[optionName] === optionValue
      );

      const availableVariant = matchingVariants.find(
        (variant) => variant.available
      );
      return availableVariant
        ? availableVariant.id
        : matchingVariants[0]?.id || null;
    }

    /**
     * Updates the add-to-cart button state based on current variant availability
     */
    updateAddToCartState() {
      const currentVariant = this.getCurrentVariant("featured");
      if (!currentVariant) return;

      this.addToCartButtons.forEach((button) => {
        const buttonModifiers = new window.zag.managers.ModifiersManager(
          button
        );
        const firstSpan = button.querySelector("span:first-child");
        if (!firstSpan) return;

        if (currentVariant.available) {
          buttonModifiers.add("button-style:solid-primary");
          buttonModifiers.remove("button-style:solid-secondary");
          buttonModifiers.remove("out-of-stock");
          firstSpan.textContent = this.data.translations.add_to_cart;
        } else {
          buttonModifiers.add("button-style:solid-secondary");
          buttonModifiers.remove("button-style:solid-primary");
          buttonModifiers.add("out-of-stock");
          firstSpan.textContent = this.data.translations.back_soon;
        }
      });
    }

    // =========================================
    // Variant Picker Handlers
    // =========================================

    handleOptionValueClick = (event) => {
      const button = event.currentTarget;

      if (button.getAttribute("aria-current") === "true") return;

      const { optionName, optionValue } = button.dataset;

      const validatedVariantId = this.validateAndSelectVariant(
        optionName,
        optionValue
      );

      if (validatedVariantId) {
        this.updateSelection("featured", validatedVariantId);
      }
    };

    // =========================================
    // Bundle Card Handlers
    // =========================================

    handleSkinCardClick = (event) => {
      event.stopPropagation();
      const card = event.currentTarget;

      if (card.getAttribute("aria-disabled") === "true") return;

      const container = card.closest(".option-list-container");
      if (container) {
        container.removeAttribute("data-change-layout");
      }

      const productId = parseInt(card.dataset.productId);
      const variantId = this.findAvailableSkinVariant(productId);

      this.updateSelection("skin", variantId);
      this.hideSkinError();
    };

    handleBindingCardClick = (event) => {
      event.stopPropagation();
      const card = event.currentTarget;

      if (card.getAttribute("aria-disabled") === "true") return;

      const isAlreadySelected = card.getAttribute("aria-selected") === "true";
      const isInMainSection = !card.closest(".bundle-popin");

      const container = card.closest(".option-list-container");
      if (container) {
        container.removeAttribute("data-change-layout");
      }

      const productId = parseInt(card.dataset.productId);
      const currentVariantId = this.getCurrentBindingCardVariantId(
        card,
        productId
      );

      this.updateSelection("binding", currentVariantId);

      if (!isInMainSection) {
        this.updateBindingGalleries();
      }

      if (isInMainSection && !isAlreadySelected) {
        this.openBundlePopin(event);
      }
    };

    getCurrentBindingCardVariantId(card, productId) {
      const product = this.data.bundle_data.binding_products[productId];
      if (!product) return null;

      const options = {};
      card
        .querySelectorAll(".binding-option-value[aria-current='true']")
        .forEach((button) => {
          const { optionName, optionValue } = button.dataset;
          options[optionName] = optionValue;
        });

      if (Object.keys(options).length === 0) {
        return this.findDefaultBindingVariantId(productId);
      }

      const variantKey = this.buildVariantKey(options);
      return product.variant_lookup[variantKey] || null;
    }

    handleBindingOptionValueClick = (event) => {
      event.stopPropagation();
      const button = event.currentTarget;
      const card = button.closest(".binding-card");

      if (button.getAttribute("aria-current") === "true") return;
      if (button.disabled) return;

      const productId = parseInt(card.dataset.productId);
      const { optionName, optionValue } = button.dataset;

      this.updateBindingCardOptions(card, productId, optionName, optionValue);

      this.syncBindingCardOptions(productId, optionName, optionValue, card);

      const selectedVariantId = this.data.selection.binding_variant?.id;
      if (selectedVariantId) {
        const selectedVariant =
          this.data.bundle_data.binding_variants[selectedVariantId];
        if (selectedVariant?.product_id === productId) {
          const currentOptions = {};
          card
            .querySelectorAll(".binding-option-value[aria-current='true']")
            .forEach((btn) => {
              const { optionName: name, optionValue: value } = btn.dataset;
              currentOptions[name] = value;
            });

          const variantKey = this.buildVariantKey(currentOptions);
          const product = this.data.bundle_data.binding_products[productId];
          const newVariantId = product.variant_lookup[variantKey];

          if (newVariantId) {
            this.data.selection.binding_variant.id = newVariantId;

            const newVariant =
              this.data.bundle_data.binding_variants[newVariantId];
            if (newVariant) {
              this.updateCardPrice(card, newVariant);
            }

            this.updateBindingGalleries();
            this.updatePrice();

            this.updateBindingCards();
          }
        }
      }

      if (card.hasAttribute("data-advanced-card")) {
        const currentOptions = {};
        card
          .querySelectorAll(".binding-option-value[aria-current='true']")
          .forEach((btn) => {
            const { optionName: name, optionValue: value } = btn.dataset;
            currentOptions[name] = value;
          });

        const variantKey = this.buildVariantKey(currentOptions);
        const variantId =
          this.data.bundle_data.binding_products[productId].variant_lookup[
            variantKey
          ];
        const variant = this.data.bundle_data.binding_variants[variantId];
      }
    };

    syncBindingCardOptions(
      productId,
      changedOptionName,
      changedOptionValue,
      excludeCard
    ) {
      this.bindingCards.forEach((card) => {
        if (card === excludeCard) return;

        const cardProductId = parseInt(card.dataset.productId);
        if (cardProductId === productId) {
          this.updateBindingCardOptions(
            card,
            productId,
            changedOptionName,
            changedOptionValue
          );
        }
      });
    }

    updateBindingCardOptions = (
      card,
      productId,
      changedOptionName,
      changedOptionValue
    ) => {
      const product = this.data.bundle_data.binding_products[productId];
      if (!product) return;

      const currentOptions = {};
      card
        .querySelectorAll(".binding-option-value[aria-current='true']")
        .forEach((button) => {
          const { optionName, optionValue } = button.dataset;
          currentOptions[optionName] = optionValue;
        });

      currentOptions[changedOptionName] = changedOptionValue;

      card.querySelectorAll(".binding-option-value").forEach((button) => {
        const { optionName, optionValue } = button.dataset;

        const testOptions = {
          ...currentOptions,
          [optionName]: optionValue,
        };

        const variantKey = this.buildVariantKey(testOptions);
        const variantId = product.variant_lookup[variantKey];
        const variant = variantId
          ? this.data.bundle_data.binding_variants[variantId]
          : null;

        const isSelected = currentOptions[optionName] === optionValue;
        const isAvailable = variant?.available || false;

        button.setAttribute("aria-current", isSelected ? "true" : "false");
        button.setAttribute("aria-disabled", isAvailable ? "false" : "true");
        button.disabled = !isAvailable;
      });

      const currentVariantId =
        product.variant_lookup[this.buildVariantKey(currentOptions)];
      this.updateCardImage(
        card,
        this.data.bundle_data.binding_variants[currentVariantId]
      );
    };

    handleCramponCardClick = (event) => {
      event.stopPropagation();
      const card = event.currentTarget;

      if (card.getAttribute("aria-disabled") === "true") return;

      const container = card.closest(".option-list-container");
      if (container) {
        container.removeAttribute("data-change-layout");
      }

      const productId = parseInt(card.dataset.productId);
      const variantId = this.findAvailableCramponVariant(productId);

      this.updateSelection("crampon", variantId);
    };

    handleDescriptionToggle = (event) => {
      event.stopPropagation();
      const toggle = event.currentTarget;
      const card = toggle.closest(".binding-card");
      const descriptionContainer = card.querySelector(
        ".option-description-container"
      );

      if (descriptionContainer.hasAttribute("open")) {
        toggle.removeAttribute("data-active");
        toggle.querySelector("p").innerHTML =
          this.data.translations?.more_information;
        descriptionContainer.removeAttribute("open");
      } else {
        toggle.setAttribute("data-active", "");
        toggle.querySelector("p").innerHTML =
          this.data.translations?.less_information;
        descriptionContainer.setAttribute("open", "");
      }
    };

    // =========================================
    // Bundle Item Management
    // =========================================

    handleChangeItemClick = (event) => {
      event.stopPropagation();
      const button = event.currentTarget;
      const container = button.closest(".option-list-container");

      if (container && container.id.includes("binding")) {
        container.setAttribute("data-change-layout", "");
        this.updateSelection("binding", null);
        this.openBundlePopin(event);
      } else if (container) {
        container.setAttribute("data-change-layout", "");
      }
    };

    handleRemoveItemClick = (event) => {
      event.stopPropagation();
      const button = event.currentTarget;
      const container = button.closest(".option-list-container");

      if (container) {
        const selectedCard = container.querySelector(
          '.option-item[aria-selected="true"]'
        );
        if (selectedCard) {
          selectedCard.click();
        }
      }
    };

    // =========================================
    // Bundle Popin Management
    // =========================================

    openBundlePopin = (event) => {
      event.preventDefault();
      window.zag.utils.documentScroll.isLocked = true;
      this.modifiers.add("bundle-popin:1");
      this.updateBindingGalleries();
    };

    closeBundlePopin = (event) => {
      if (event) event.preventDefault();
      window.zag.utils.documentScroll.isLocked = false;
      this.modifiers.remove("bundle-popin:1", "bundle-popin:2");

      this.mountingVideos.forEach((video) => {
        video.pause();
        video.currentTime = 0;

        this.mountingVideoPlaying = false;

        this.updateMountingVideoButton();
      });
    };

    handlePopinBackgroundClick = (event) => {
      if (event.target === this.bundlePopinContainer) {
        this.closeBundlePopin();
      }
    };

    handleNextPopinStep = (event) => {
      event.preventDefault();

      if (!this.data.selection.binding_variant?.id) {
        this.showBindingValidationError();
        return;
      }

      this.modifiers.remove("bundle-popin:1");
      this.modifiers.add("bundle-popin:2");
    };

    handlePreviousPopinStep = (event) => {
      event.preventDefault();

      this.updateSelection("mounting", null);

      this.modifiers.add("bundle-popin:1");
      this.modifiers.remove("bundle-popin:2");

      this.mountingVideos.forEach((video) => {
        video.pause();
        video.currentTime = 0;

        this.mountingVideoPlaying = false;

        this.updateMountingVideoButton();
      });

      this.mountingInfoError?.setAttribute("hidden", "");
      this.mountingInfoCheckbox?.removeAttribute("aria-invalid");
    };

    handleFinishBundleConfiguration = (event) => {
      event.preventDefault();

      let hasError = false;

      const customerLevel = this.customerLevelSelect?.value;
      const emptyCustomerLevel = !customerLevel || customerLevel === "";

      if (emptyCustomerLevel) {
        const emptyMessage =
          this.customerLevelSelect.parentElement.getAttribute("data-empty");
        this.customerLevelLabel.textContent = emptyMessage;
        hasError = true;
      } else {
        this.customerLevelLabel.textContent = "";
      }

      const customerAge = this.customerAgeSelect?.value;
      const emptyCustomerAge = !customerAge || customerAge === "";

      if (emptyCustomerAge) {
        const emptyMessage =
          this.customerAgeSelect.parentElement.getAttribute("data-empty");
        this.customerAgeLabel.textContent = emptyMessage;
        hasError = true;
      } else {
        this.customerAgeLabel.textContent = "";
      }

      const customerHeight = parseFloat(this.customerHeightInput?.value);
      const emptyCustomerHeight = isNaN(customerHeight);
      const minCustomerHeight = parseFloat(
        this.customerHeightInput?.getAttribute("min")
      );
      const maxCustomerHeight = parseFloat(
        this.customerHeightInput?.getAttribute("max")
      );
      const outOfBoundsCustomerHeight =
        customerHeight < minCustomerHeight ||
        (maxCustomerHeight && customerHeight > maxCustomerHeight);

      if (emptyCustomerHeight) {
        const emptyMessage =
          this.customerHeightInput.parentElement.getAttribute("data-empty");
        this.customerHeightLabel.textContent = emptyMessage;
        hasError = true;
      } else if (outOfBoundsCustomerHeight) {
        const errorMessage =
          this.customerHeightInput.parentElement.getAttribute("data-error");
        this.customerHeightLabel.textContent = errorMessage;
        hasError = true;
      } else {
        this.customerHeightLabel.textContent = "";
      }

      const customerWeight = parseFloat(this.customerWeightInput?.value);
      const emptyCustomerWeight = isNaN(customerWeight);
      const minCustomerWeight = parseFloat(
        this.customerWeightInput?.getAttribute("min")
      );
      const maxCustomerWeight = parseFloat(
        this.customerWeightInput?.getAttribute("max")
      );
      const outOfBoundsCustomerWeight =
        customerWeight < minCustomerWeight ||
        (maxCustomerWeight && customerWeight > maxCustomerWeight);

      if (emptyCustomerWeight) {
        const emptyMessage =
          this.customerWeightInput.parentElement.getAttribute("data-empty");
        this.customerWeightLabel.textContent = emptyMessage;
        hasError = true;
      } else if (outOfBoundsCustomerWeight) {
        const errorMessage =
          this.customerWeightInput.parentElement.getAttribute("data-error");
        this.customerWeightLabel.textContent = errorMessage;
        hasError = true;
      } else {
        this.customerWeightLabel.textContent = "";
      }

      const bootLength = parseFloat(this.bootLengthInput?.value);
      const emptyBootLength = isNaN(bootLength);
      const minBootLength = parseFloat(
        this.bootLengthInput?.getAttribute("min")
      );
      const maxBootLength = parseFloat(
        this.bootLengthInput?.getAttribute("max")
      );
      const outOfBoundsBootLength =
        bootLength < minBootLength || bootLength > maxBootLength;

      if (emptyBootLength) {
        const emptyMessage =
          this.bootLengthInput.parentElement.getAttribute("data-empty");
        this.bootLengthLabel.textContent = emptyMessage;
        hasError = true;
      } else if (outOfBoundsBootLength) {
        const errorMessage =
          this.bootLengthInput.parentElement.getAttribute("data-error");
        this.bootLengthLabel.textContent = errorMessage;
        hasError = true;
      } else {
        this.bootLengthLabel.textContent = "";
      }

      const mountingInfoChecked = this.mountingInfoCheckbox?.checked ?? true;
      if (!mountingInfoChecked) {
        this.mountingInfoError?.removeAttribute("hidden");
        this.mountingInfoCheckbox?.setAttribute("aria-invalid", "true");
        this.mountingInfoCheckbox?.addEventListener(
          "change",
          () => {
            this.mountingInfoError?.setAttribute("hidden", "");
            this.mountingInfoCheckbox?.removeAttribute("aria-invalid");
          },
          { once: true }
        );
        hasError = true;
      }

      if (hasError) return;

      const mountingVariantId = this.data.bundle_data.mounting_variant?.id;
      this.updateSelection("mounting", mountingVariantId, {
        configuration: {
          customer_level: customerLevel,
          customer_age: customerAge,
          customer_height: customerHeight,
          customer_weight: customerWeight,
          boot_length: bootLength,
        },
      });

      this.closeBundlePopin();
    };

    // =========================================
    // Add to Cart
    // =========================================

    handleAddToCart = async (event) => {
      event.preventDefault();

      if (event.currentTarget.hasAttribute("disabled")) return;

      if (this.data.is_bundle_context && this.hasBundleSelection()) {
        this.showAddToCartConfirmation();
        return;
      }

      await this.addToCart();
    };

    async addToCart() {
      try {
        this.addToCartButtons.forEach((button) => {
          button.setAttribute("disabled", "true");
        });

        const items = await this.buildCartItems();
        const headerSectionId = window.zag.dom.header.section.id
          .split("shopify-section-")
          .at(-1);

        const formData = {
          items: items,
          sections: [headerSectionId],
        };

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(formData),
        });

        const result = await response.json();

        if (window.zag.dom.miniCart) {
          window.zag.dom.miniCart.dispatchEvent(
            new CustomEvent("zag-cart:updated", {
              detail: result.sections[headerSectionId],
            })
          );
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
      } finally {
        this.addToCartButtons.forEach((button) => {
          button.removeAttribute("disabled");
        });
      }
    }

    async buildCartItems() {
      const items = [];

      const featuredVariantData = this.data.selection.featured_variant;
      const featuredVariant =
        this.data.featured_product.variants[featuredVariantData.id];

      const bundleConfiguration =
        this.data.is_bundle_context &&
        Object.values(this.data.selection)
          .map((value) => {
            return value.id;
          })
          .slice(1)
          .some((id) => id !== undefined && id !== null);

      if (!bundleConfiguration) {
        items.push({
          id: featuredVariantData.id,
          quantity: featuredVariant?.quantity || 1,
        });

        return items;
      }

      const bundleId = await this.getBundleId();

      const featuredVariantProperties = {};

      const mountingVariantData = this.data.selection.mounting_variant;
      if (mountingVariantData?.id) {
        const mountingVariant = this.data.bundle_data.mounting_variant;
        const mountingConfigurationData =
          this.data.selection.mounting_configuration;

        featuredVariantProperties[`_mountingId`] = mountingVariant.id;

        items.push({
          id: mountingVariant.id,
          quantity: mountingVariantData?.quantity || 1,
          properties: {
            _bundleId: bundleId,
            _customerLevel: mountingConfigurationData.customer_level,
            _customerAge: mountingConfigurationData.customer_age,
            _customerHeight: mountingConfigurationData.customer_height,
            _mountingWeight: mountingConfigurationData.customer_weight,
            _mountingLength: mountingConfigurationData.boot_length,
          },
        });
      }

      ["crampon", "skin", "binding"].forEach((type) => {
        const variantData = this.data.selection[`${type}_variant`];

        if (variantData?.id !== null) {
          const variant =
            this.data.bundle_data[`${type}_variants`][variantData.id];

          featuredVariantProperties[`_${type}Id`] = variant.id;

          items.unshift({
            id: variant.id,
            quantity: variantData?.quantity || 1,
            properties: {
              _bundleId: bundleId,
            },
          });
        }
      });

      items.unshift({
        id: featuredVariant.id,
        quantity: featuredVariantData?.quantity || 1,
        properties: {
          ...featuredVariantProperties,
          _bundleId: bundleId,
        },
      });

      return items.reverse();
    }

    async getBundleId() {
      const featuredVariantId = this.data.selection.featured_variant?.id;
      const featuredVariant =
        this.data.featured_product.variants[featuredVariantId];
      const featuredSku = featuredVariant?.sku;

      let baseBundleId = `${featuredSku}`;

      for (const type of ["binding", "mounting"]) {
        const variantId = this.data.selection[`${type}_variant`]?.id;

        if (variantId) {
          const variantData =
            type === "mounting"
              ? this.data.bundle_data.mounting_variant
              : this.data.bundle_data[`${type}_variants`][variantId];

          const variantSku = variantData?.sku;

          if (variantSku) {
            baseBundleId += `_${variantSku}`;
          }
        }
      }

      return await window.zag.utils.getUniqueBundleId(baseBundleId);
    }

    hasBundleSelection() {
      return ["binding", "skin", "crampon", "mounting"].some(
        (type) => this.data.selection[`${type}_variant`]?.id
      );
    }

    showAddToCartConfirmation() {
      // TODO: Implement confirmation modal

      this.addToCart();
    }

    // =========================================
    // UI Updates
    // =========================================

    updateUI() {
      this.updateOptionValueButtons();
      this.updateSizeOptionsPerLine();
      this.updateAddToCartState();

      if (this.data.is_bundle_context) {
        this.updateSkinCards();
        this.updateBindingCards();
        this.updateCramponCards();
        this.updateBindingGalleries();
      }
    }

    updateOptionValueButtons() {
      const currentVariant = this.getCurrentVariant("featured");
      if (!currentVariant) return;

      this.optionValues.forEach((button) => {
        const { optionName, optionValue } = button.dataset;

        const testOptions = {
          ...currentVariant.options,
          [optionName]: optionValue,
        };

        const variantKey = this.buildVariantKey(testOptions);
        const variantId = this.data.featured_product.variant_lookup[variantKey];
        const variant = variantId
          ? this.data.featured_product.variants[variantId]
          : null;

        const isSelected = currentVariant.options[optionName] === optionValue;

        if (variant) {
          // Real variant exists
          const isAvailable = variant.available;
          button.setAttribute("aria-disabled", isAvailable ? "false" : "true");
          if (isAvailable) {
            button.removeAttribute("data-unavailable-reason");
          } else {
            button.setAttribute("data-unavailable-reason", "out-of-stock");
          }
        } else {
          // Impossible variant combination
          button.setAttribute("aria-disabled", "true");
          button.setAttribute("data-unavailable-reason", "impossible");
        }

        button.setAttribute("aria-current", isSelected ? "true" : "false");
      });
    }

    updateSizeOptionsPerLine() {
      if (
        !this.querySelector(
          '.variant-picker-container:not(.hidden-variant-picker):where([data-option-name*="Color"], [data-option-name*="Couleur"])'
        )
      ) {
        return;
      }

      const sizeOptionName = this.data.option_names.size_option;

      if (!sizeOptionName) return;

      const sizeOptionContainer = this.querySelector(
        `.variant-picker-container[data-option-name="${sizeOptionName}"]`
      );

      if (!sizeOptionContainer) return;

      const optionValuesContainer =
        sizeOptionContainer.querySelector(".option-values");

      if (!optionValuesContainer) return;

      const visibleSizeOptionButtons = Array.from(
        optionValuesContainer.querySelectorAll(".option-value")
      ).filter((button) => {
        const isDisabled = button.getAttribute("aria-disabled") === "true";
        return !isDisabled;
      });

      const visibleCount = visibleSizeOptionButtons.length;

      const optionsPerLine = Math.max(3, visibleCount);

      const allSizeOptionButtons =
        optionValuesContainer.querySelectorAll(".option-value");
      allSizeOptionButtons.forEach((button) => {
        button.style.setProperty("--options-per-line", optionsPerLine);
      });
    }

    updateSkinCards() {
      const sizeOptionName = this.data.option_names.size_option;
      const currentSize =
        this.getCurrentVariant("featured")?.options[sizeOptionName];
      const selectedId = this.data.selection.skin_variant?.id;

      this.skinCards.forEach((card) => {
        const productId = parseInt(card.dataset.productId);
        const product = this.data.bundle_data.skin_products[productId];

        const availableVariant = this.findSkinVariantForSize(
          product,
          currentSize
        );

        const isSelected =
          selectedId &&
          this.data.bundle_data.skin_variants[selectedId]?.product_id ===
            productId;
        const isAvailable = availableVariant?.available || false;

        card.setAttribute("aria-selected", isSelected ? "true" : "false");
        card.setAttribute("aria-disabled", isAvailable ? "false" : "true");

        const variantToPrice = isSelected
          ? this.data.bundle_data.skin_variants[selectedId]
          : availableVariant;

        if (variantToPrice) {
          this.updateCardPrice(card, variantToPrice);
        }
      });
    }

    updateBindingCards() {
      const selectedId = this.data.selection.binding_variant?.id;

      this.bindingCards.forEach((card) => {
        const productId = parseInt(card.dataset.productId);

        const isSelected =
          selectedId &&
          this.data.bundle_data.binding_variants[selectedId]?.product_id ===
            productId;

        const hasAvailableVariant = Object.values(
          this.data.bundle_data.binding_variants
        ).some(
          (variant) => variant.product_id === productId && variant.available
        );

        card.setAttribute("aria-selected", isSelected ? "true" : "false");
        card.setAttribute(
          "aria-disabled",
          hasAvailableVariant ? "false" : "true"
        );

        if (isSelected && selectedId) {
          this.updateBindingOptionButtons(card, selectedId);

          const selectedVariant =
            this.data.bundle_data.binding_variants[selectedId];
          if (selectedVariant) {
            this.updateCardPrice(card, selectedVariant);
          }
        } else if (hasAvailableVariant) {
          const defaultVariantId = this.findDefaultBindingVariant(productId);
          if (defaultVariantId) {
            const defaultVariant =
              this.data.bundle_data.binding_variants[defaultVariantId];
            if (defaultVariant) {
              this.updateCardPrice(card, defaultVariant);
            }
          }
        }

        const reassuranceContainer = card.querySelector(
          ".option-reassurance-container"
        );
        if (reassuranceContainer) {
          if (isSelected) {
            reassuranceContainer.setAttribute("open", "");
          } else {
            reassuranceContainer.removeAttribute("open");
          }
        }

        const mountingDataElement = card.querySelector(".mounting-data");
        if (mountingDataElement) {
          this.updateMountingDataDisplay(mountingDataElement);
        }

        const optionValues = Array.from(
          card.querySelectorAll(".binding-option-value[aria-current='true']")
        );

        optionValues.forEach((button) => {
          const { optionName, optionValue } = button.dataset;
          const optionElement = card.querySelector(
            `.option-mounting-data [data-option-name="${optionName}"]`
          );

          if (optionElement) {
            optionElement.textContent = `${optionName}: ${optionValue}`;
          }
        });
      });
    }

    updateMountingDataDisplay(mountingDataElement) {
      const mountingConfiguration = this.data.selection.mounting_configuration;
      const defaultText = mountingDataElement.getAttribute("data-translation");

      const colonIndex = defaultText ? defaultText.indexOf(":") : -1;
      const headerPart =
        colonIndex !== -1
          ? defaultText.substring(0, colonIndex + 1)
          : defaultText || "Mounting info:";

      if (
        mountingConfiguration &&
        mountingConfiguration.customer_level &&
        mountingConfiguration.customer_age &&
        mountingConfiguration.customer_height &&
        mountingConfiguration.customer_weight &&
        mountingConfiguration.boot_length
      ) {
        const level = mountingConfiguration.customer_level;
        const age = mountingConfiguration.customer_age;
        const height = mountingConfiguration.customer_height;
        const weight = mountingConfiguration.customer_weight;
        const bootLength = mountingConfiguration.boot_length;

        const heightUnit = "cm";
        const weightUnit = "kg";
        const lengthUnit = "mm";

        const mountingText = `${headerPart} ${height}${heightUnit} - ${weight}${weightUnit} - ${bootLength}${lengthUnit}`;
        mountingDataElement.innerHTML = mountingText;
      } else {
        mountingDataElement.innerHTML = defaultText || "Mounting info";
      }
    }

    updateBindingOptionButtons(card, selectedVariantId) {
      const selectedVariant =
        this.data.bundle_data.binding_variants[selectedVariantId];
      const productId = parseInt(card.dataset.productId);
      const product = this.data.bundle_data.binding_products[productId];

      card.querySelectorAll(".binding-option-value").forEach((button) => {
        const { optionName, optionValue } = button.dataset;

        const variantOptions = {
          ...selectedVariant.options,
          [optionName]: optionValue,
        };

        const variantKey = this.buildVariantKey(variantOptions);
        const variantId = product.variant_lookup[variantKey];
        const variant = variantId
          ? this.data.bundle_data.binding_variants[variantId]
          : null;

        const isSelected = selectedVariant.options[optionName] === optionValue;
        const isAvailable = variant?.available || false;

        button.setAttribute("aria-current", isSelected ? "true" : "false");
        button.setAttribute("aria-disabled", isAvailable ? "false" : "true");
        button.disabled = !isAvailable;
      });

      this.updateCardImage(card, selectedVariant);
    }

    updateCramponCards() {
      const selectedId = this.data.selection.crampon_variant?.id;

      this.cramponCards.forEach((card) => {
        const productId = parseInt(card.dataset.productId);

        const isSelected =
          selectedId &&
          this.data.bundle_data.crampon_variants[selectedId]?.product_id ===
            productId;

        const isVisible = card.dataset.visible === "true";
        const hasAvailableVariant = this.hasCramponAvailableVariant(productId);

        card.setAttribute("aria-selected", isSelected ? "true" : "false");
        card.setAttribute(
          "aria-disabled",
          !isVisible || !hasAvailableVariant ? "true" : "false"
        );

        if (isVisible && hasAvailableVariant) {
          if (isSelected && selectedId) {
            const selectedVariant =
              this.data.bundle_data.crampon_variants[selectedId];
            if (selectedVariant) {
              this.updateCardPrice(card, selectedVariant);
            }
          } else {
            const defaultVariantId =
              this.findAvailableCramponVariant(productId);
            if (defaultVariantId) {
              const defaultVariant =
                this.data.bundle_data.crampon_variants[defaultVariantId];
              if (defaultVariant) {
                this.updateCardPrice(card, defaultVariant);
              }
            }
          }
        }
      });
    }

    updateCramponVisibility = (bindingVariantId) => {
      if (!bindingVariantId) {
        this.hideAllCramponCards();
        if (this.data.selection.crampon_variant?.id) {
          this.data.selection.crampon_variant.id = null;
        }
        return;
      }

      const bindingVariant =
        this.data.bundle_data.binding_variants[bindingVariantId];
      if (!bindingVariant) {
        this.hideAllCramponCards();
        if (this.data.selection.crampon_variant?.id) {
          this.data.selection.crampon_variant.id = null;
        }
        return;
      }

      const bindingProduct =
        this.data.bundle_data.binding_products[bindingVariant.product_id];
      if (!bindingProduct) {
        this.hideAllCramponCards();
        if (this.data.selection.crampon_variant?.id) {
          this.data.selection.crampon_variant.id = null;
        }
        return;
      }

      const bindingCompatibleCramponVariantIds =
        bindingProduct.compatible_crampon_variants || [];

      this.cramponCards.forEach((card) => {
        const cramponProductId = parseInt(card.dataset.productId);
        const cramponProduct =
          this.data.bundle_data.crampon_products[cramponProductId];

        if (!cramponProduct) {
          card.dataset.visible = "false";
          return;
        }

        const cramponCompatibleVariantIds =
          Object.values(cramponProduct.variant_lookup) || [];

        const eligibleCramponVariantIds = cramponCompatibleVariantIds.filter(
          (variantId) => bindingCompatibleCramponVariantIds.includes(variantId)
        );

        const hasCompatibleVariant = eligibleCramponVariantIds.some(
          (variantId) => {
            const variant = this.data.bundle_data.crampon_variants[variantId];
            return variant?.available;
          }
        );

        card.dataset.visible = hasCompatibleVariant ? "true" : "false";
      });
    };

    hideAllCramponCards = () => {
      this.cramponCards.forEach((card) => {
        card.dataset.visible = "false";
      });
    };

    updateBindingGalleries() {
      const selectedBindingId = this.data.selection.binding_variant?.id;
      if (!selectedBindingId) return;

      const bindingVariant =
        this.data.bundle_data.binding_variants[selectedBindingId];
      const bindingProductId = bindingVariant?.product_id;
      const bindingProduct =
        this.data.bundle_data.binding_products[bindingProductId];

      if (!bindingProduct) return;

      const colorOptionName = this.data.option_names.color_option;

      const featuredVariantId = this.data.selection.featured_variant?.id;
      const featuredVariant =
        this.data.featured_product.variants[featuredVariantId];

      const skiColor = featuredVariant?.options[colorOptionName];
      if (!skiColor) return;

      const bindingColor = bindingVariant?.options[colorOptionName] || "";

      const targetGalleryId = bindingProduct.gallery_id_list?.find(
        (galleryId) => {
          return (
            galleryId === `${bindingProductId}:${skiColor}:${bindingColor}`
          );
        }
      );

      const fallbackGallery =
        this?.querySelector("[data-default-gallery]") ||
        this.querySelector(".skib-binding-gallery-container");
      const fallbackGalleryId = fallbackGallery?.dataset.galleryId;

      const finalGalleryId = targetGalleryId || fallbackGalleryId;
      if (!finalGalleryId) return;

      const galleryList = this.querySelectorAll(
        ".ski-binding-gallery-container"
      );
      galleryList.forEach((gallery) => {
        gallery.dataset.galleryId === finalGalleryId
          ? gallery.setAttribute("aria-current", "true")
          : gallery.setAttribute("aria-current", "false");
      });

      const activeGallery = this.querySelector(
        ".ski-binding-gallery-container[aria-current='true']"
      );

      if (!activeGallery) {
        const defaultGallery = this.querySelector(
          ".ski-binding-gallery-container[data-default-gallery]"
        );
        defaultGallery?.setAttribute("aria-current", "true");
      }
    }

    updatePrice() {
      let totalPrice = 0;

      const featuredVariant = this.getCurrentVariant("featured");
      if (featuredVariant) {
        totalPrice += featuredVariant.price;
      }

      if (this.data.is_bundle_context) {
        ["binding", "skin", "crampon", "mounting"].forEach((type) => {
          const variantId = this.data.selection[`${type}_variant`]?.id;
          if (variantId) {
            const variant =
              this.data.bundle_data[`${type}_variants`]?.[variantId];
            if (variant) {
              totalPrice += variant.price;
            }
          }
        });
      }

      this.prices.forEach((priceElement) => {
        if (priceElement.renderPrice) {
          priceElement.renderPrice(totalPrice);
        }
      });
    }

    // =========================================
    // Error Handling
    // =========================================

    showSkinSizeError() {
      const errorContainer = this.querySelector(
        ".option-list-container:has(.skin-card)"
      );
      if (errorContainer) {
        errorContainer.setAttribute("data-show-error", "");
      }
    }

    hideSkinError() {
      const errorContainer = this.querySelector(
        ".option-list-container:has(.skin-card)"
      );
      if (errorContainer) {
        errorContainer.removeAttribute("data-show-error");
      }
    }

    showBindingValidationError() {
      this.bindingCards.forEach((card) => {
        card.setAttribute("data-validation-error", "");
        setTimeout(() => {
          card.removeAttribute("data-validation-error");
        }, 500);
      });
    }

    showBootLengthError() {
      const input = this.bootLengthInput;
      if (input) {
        input.style.borderColor = "#e30000";
        const label = input.nextElementSibling;
        if (label) {
          label.style.display = "block";
        }
      }
    }

    hideBootLengthError() {
      const input = this.bootLengthInput;
      if (input) {
        input.style.borderColor = "";
        const label = input.nextElementSibling;
        if (label) {
          label.style.display = "none";
        }
      }
    }

    showHeightError() {
      const input = this.customerHeightInput;
      if (input) {
        input.style.borderColor = "#e30000";
        const label = input.nextElementSibling;
        if (label) {
          label.style.display = "block";
        }
      }
    }

    hideHeightError() {
      const input = this.customerHeightInput;
      if (input) {
        input.style.borderColor = "";
        const label = input.nextElementSibling;
        if (label) {
          label.style.display = "none";
        }
      }
    }

    showWeightError() {
      const input = this.customerWeightInput;
      if (input) {
        input.style.borderColor = "#e30000";
        const label = input.nextElementSibling;
        if (label) {
          label.style.display = "block";
        }
      }
    }

    hideWeightError() {
      const input = this.customerWeightInput;
      if (input) {
        input.style.borderColor = "";
        const label = input.nextElementSibling;
        if (label) {
          label.style.display = "none";
        }
      }
    }

    // =========================================
    // Input Validation
    // =========================================

    validateBootLength = (event) => {
      const value = parseFloat(event.target.value);
      if (this.isValidBootLength(value)) {
        this.hideBootLengthError();
      } else {
        this.showBootLengthError();
      }
    };

    validateHeight = (event) => {
      const value = parseFloat(event.target.value);
      if (this.isValidHeight(value)) {
        this.hideHeightError();
      } else {
        this.showHeightError();
      }
    };

    validateWeight = (event) => {
      const value = parseFloat(event.target.value);
      if (this.isValidWeight(value)) {
        this.hideWeightError();
      } else {
        this.showWeightError();
      }
    };

    isValidBootLength(value) {
      const min = parseInt(this.bootLengthInput?.min) || 20;
      const max = parseInt(this.bootLengthInput?.max) || 150;
      return !isNaN(value) && value >= min && value <= max;
    }

    isValidHeight(value) {
      const min = parseInt(this.customerHeightInput?.min) || 0;
      const max = parseInt(this.customerHeightInput?.max) || 999;
      return !isNaN(value) && value >= min && (max === 0 || value <= max);
    }

    isValidWeight(value) {
      const min = parseInt(this.customerWeightInput?.min) || 20;
      const max = parseInt(this.customerWeightInput?.max) || 150;
      return !isNaN(value) && value >= min && value <= max;
    }

    // =========================================
    // Gallery Video Management
    // =========================================

    handleGallerySlideChange = (event) => {
      const { slide: activeSlide, index: activeIndex } = event.detail;

      this.pauseAllGalleryVideos();
      this.handleActiveSlideMedia(activeSlide);
    };

    // =========================================
    // Gallery Zoom Management
    // =========================================

    handleMediaItemHover = (event) => {
      if (window.innerWidth < 1280) return;

      const mediaItem = event.currentTarget;
      const zoomImage = mediaItem.querySelector(".media-item--zoom");

      if (
        !zoomImage ||
        !mediaItem.hasAttribute("data-modifiers") ||
        !mediaItem.getAttribute("data-modifiers").includes("image")
      ) {
        return;
      }

      if (this.zoomThrottleId) {
        cancelAnimationFrame(this.zoomThrottleId);
      }

      this.zoomThrottleId = requestAnimationFrame(() => {
        this.updateZoomPosition(event, mediaItem, zoomImage);
      });
    };

    handleMediaItemLeave = (event) => {
      if (this.zoomThrottleId) {
        cancelAnimationFrame(this.zoomThrottleId);
        this.zoomThrottleId = null;
      }

      const mediaItem = event.currentTarget;
      const zoomImage = mediaItem.querySelector(".media-item--zoom");

      if (zoomImage) {
        zoomImage.style.translate = "";
      }
    };

    updateZoomPosition = (event, mediaItem, zoomImage) => {
      const rect = mediaItem.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const relativeX = x / rect.width;
      const relativeY = y / rect.height;

      const clampedX = Math.max(0, Math.min(1, relativeX));
      const clampedY = Math.max(0, Math.min(1, relativeY));

      const translateXPercent = -clampedX * 100;
      const translateYPercent = -clampedY * 100;

      zoomImage.style.translate = `${translateXPercent}% ${translateYPercent}%`;
    };

    pauseAllGalleryVideos = () => {
      this.galleryVideos.forEach((video) => {
        if (!video.paused) {
          video.pause();
        }
        this.handleVideoLooping(video, false);

        const videoContainer = video.closest(".media-item");
        if (videoContainer) {
          this.updateVideoIconState(videoContainer, false);
        }
      });

      this.galleryExternalVideos.forEach((iframe) => {
        this.pauseExternalVideo(iframe);
      });
    };

    handleActiveSlideMedia = (activeSlide) => {
      const activeVideo = activeSlide.querySelector("video");
      if (activeVideo && activeVideo.hasAttribute("autoplay")) {
        const appropriateVideo = this.getAppropriateVideo(activeSlide);
        if (appropriateVideo) {
          appropriateVideo.play().catch((err) => {
            console.warn("Failed to autoplay video:", err);
          });

          this.updateVideoIconState(activeSlide, true);
          this.handleVideoLooping(appropriateVideo, true);
        }
      }

      const activeExternalVideo = activeSlide.querySelector("iframe");
      if (activeExternalVideo && activeExternalVideo.hasAttribute("autoplay")) {
        this.playExternalVideo(activeExternalVideo);
      }
    };

    handleGalleryVideoToggle = (event) => {
      const button = event.currentTarget;
      const videoContainer = button.closest(".media-item");
      const videos = Array.from(videoContainer.querySelectorAll("video"));

      if (videos.length === 0) return;

      const isAnyVideoPlaying = videos.some((video) => !video.paused);

      if (isAnyVideoPlaying) {
        videos.forEach((video) => {
          video.pause();
          this.handleVideoLooping(video, false);
        });
        this.updateVideoIconState(videoContainer, false);
      } else {
        const appropriateVideo = this.getAppropriateVideo(videoContainer);
        if (appropriateVideo) {
          appropriateVideo.play().catch((err) => {
            console.warn("Failed to play video:", err);
          });
          this.updateVideoIconState(videoContainer, true);
          this.handleVideoLooping(appropriateVideo, true);
        }
      }
    };

    getAppropriateVideo = (videoContainer) => {
      const videos = videoContainer.querySelectorAll("video");
      const isMobile = window.innerWidth < 1280;

      const mobileVideo = videoContainer.querySelector(".media-video--mobile");
      const desktopVideo = videoContainer.querySelector(
        ".media-video--desktop"
      );

      if (isMobile && mobileVideo) {
        return mobileVideo;
      } else if (!isMobile && desktopVideo) {
        return desktopVideo;
      }

      return videos[0] || null;
    };

    updateVideoIconState = (videoContainer, isPlaying) => {
      const playIcon = videoContainer.querySelector(".play__icon");
      const pauseIcon = videoContainer.querySelector(".pause__icon");

      if (isPlaying) {
        if (playIcon) playIcon.style.display = "none";
        if (pauseIcon) pauseIcon.style.display = "block";
      } else {
        if (playIcon) playIcon.style.display = "block";
        if (pauseIcon) pauseIcon.style.display = "none";
      }
    };

    handleVideoLooping = (video, isPlaying) => {
      const videoContainer = video.closest(".media-item");
      if (!videoContainer) return;

      const isShortVideo =
        videoContainer.dataset.modifiers &&
        videoContainer.dataset.modifiers.includes("short");

      if (isPlaying) {
        if (isShortVideo && !video.hasAttribute("data-loop-count")) {
          video.setAttribute("data-loop-count", "0");
        }

        const endedHandler = () => {
          if (isShortVideo) {
            let loopCount = parseInt(
              video.getAttribute("data-loop-count") || "0"
            );
            loopCount++;
            video.setAttribute("data-loop-count", loopCount);

            if (loopCount >= 3) {
              video.pause();
              video.removeEventListener("ended", endedHandler);
              video.removeAttribute("data-loop-count");
              delete video._videoEndedHandler;
              this.updateVideoIconState(videoContainer, false);
            } else {
              video.currentTime = 0;
              video.play().catch((err) => {
                console.warn("Failed to replay short video:", err);
              });
            }
          } else {
            this.updateVideoIconState(videoContainer, false);
            video.removeEventListener("ended", endedHandler);
            delete video._videoEndedHandler;
          }
        };

        video._videoEndedHandler = endedHandler;
        video.addEventListener("ended", endedHandler);
      } else {
        if (video._videoEndedHandler) {
          video.removeEventListener("ended", video._videoEndedHandler);
          delete video._videoEndedHandler;
          if (isShortVideo) {
            video.removeAttribute("data-loop-count");
          }
        }
      }
    };

    pauseExternalVideo = (iframe) => {
      const src = iframe.src;
      if (src.includes("youtube.com") || src.includes("youtu.be")) {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            event: "command",
            func: "pauseVideo",
            args: "",
          }),
          "*"
        );
      } else if (src.includes("vimeo.com")) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ method: "pause" }),
          "*"
        );
      }
    };

    playExternalVideo = (iframe) => {
      const src = iframe.src;
      if (src.includes("youtube.com") || src.includes("youtu.be")) {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            event: "command",
            func: "playVideo",
            args: "",
          }),
          "*"
        );
      } else if (src.includes("vimeo.com")) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ method: "play" }),
          "*"
        );
      }
    };

    // =========================================
    // Utility Methods
    // =========================================

    getCurrentVariant(type) {
      const variantId = this.data.selection[`${type}_variant`]?.id;

      if (type === "featured") {
        return this.data.featured_product.variants[variantId];
      }

      return this.data.bundle_data[`${type}_variants`]?.[variantId];
    }

    buildVariantKey(options) {
      return Object.entries(options)
        .map(([key, value]) => `${key}:${value}`)
        .join(";");
    }

    navigateToVariantMedia(variant) {
      if (!variant?.media_id) {
        return;
      }

      const gallerySlider = this.gallerySlider;
      if (!gallerySlider) {
        return;
      }

      const slideIndex = this.findGallerySlideByMediaId(variant.media_id);
      if (slideIndex !== -1) {
        gallerySlider.select(slideIndex);
      }
    }

    findGallerySlideByMediaId(mediaId) {
      const gallerySlider = this.gallerySlider;
      if (!gallerySlider) {
        return -1;
      }

      const slides = gallerySlider.slideList;

      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        const modifiersManager = new window.zag.managers.ModifiersManager(
          slide
        );

        if (modifiersManager.has(`id:${mediaId}`)) {
          return i;
        }
      }

      return -1;
    }

    formatPrice(price, hideDecimals = true) {
      const priceInUnits = parseFloat(price / 100);
      const isWholeNumber = priceInUnits % 1 === 0;
      const shouldHideDecimals = hideDecimals && isWholeNumber;

      const formatter = new Intl.NumberFormat(
        `${this.locale}-${this.country}`,
        {
          style: "currency",
          currency: this.currency,
          maximumFractionDigits: shouldHideDecimals ? 0 : 2,
        }
      );
      return formatter.format(priceInUnits);
    }

    updateCardPrice(card, variant) {
      const priceElement = card.querySelector(".option-price");
      if (!priceElement || !variant) return;

      const compareSpan = priceElement.querySelector("span");
      const formattedPrice = `+ ${this.formatPrice(variant.price)}`;

      if (compareSpan) {
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          compareSpan.setAttribute("data-compare", "");
          compareSpan.textContent = `+ ${this.formatPrice(
            variant.compare_at_price
          )}`;
        } else {
          compareSpan.removeAttribute("data-compare");
          compareSpan.textContent = "";
        }
      }

      Array.from(priceElement.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.remove();
        }
      });

      priceElement.appendChild(document.createTextNode(formattedPrice));
    }

    updateCardImage(card, variant) {
      const imageElement = card.querySelector(".image-container img");
      if (!imageElement || !variant?.image) return;
      if (imageElement.getAttribute("src") === variant.image) return;

      imageElement.src = variant.image;
    }

    findAvailableSkinVariant(productId) {
      const product = this.data.bundle_data.skin_products[productId];
      if (!product) return null;

      const sizeOptionName = this.data.option_names.size_option;
      const currentSize =
        this.getCurrentVariant("featured")?.options[sizeOptionName];

      if (currentSize) {
        const variant = this.findSkinVariantForSize(product, currentSize);
        if (variant?.available) {
          return variant.id;
        }
      }

      const defaultVariant =
        this.data.bundle_data.skin_variants[product.default_variant_id];
      return defaultVariant?.available ? product.default_variant_id : null;
    }

    findSkinVariantForSize(product, size) {
      if (!product || !size) return null;

      const sizeOptionName = this.data.option_names.size_option;
      const matchingVariants = Object.values(product.variant_lookup)
        .map((variantId) => this.data.bundle_data.skin_variants[variantId])
        .filter(
          (variant) =>
            variant &&
            this.skinSizeMatches(variant.options[sizeOptionName], size)
        );

      // Prefer an available variant when several skins cover the same ski size
      return (
        matchingVariants.find((variant) => variant.available) ||
        matchingVariants[0] ||
        null
      );
    }

    // Skin size can be a single ski size ("168") or a range covering
    // several ski sizes ("168-175"), matched inclusively.
    skinSizeMatches(skinSize, skiSize) {
      if (skinSize == null || skiSize == null) return false;
      if (String(skinSize).trim() === String(skiSize).trim()) return true;

      const skiValue = parseFloat(String(skiSize).replace(",", "."));
      const bounds = (String(skinSize).match(/\d+(?:[.,]\d+)?/g) || []).map(
        (value) => parseFloat(value.replace(",", "."))
      );
      if (Number.isNaN(skiValue) || bounds.length < 2) return false;

      return skiValue >= Math.min(...bounds) && skiValue <= Math.max(...bounds);
    }

    findDefaultBindingVariant(productId) {
      const product = this.data.bundle_data.binding_products[productId];
      return product?.default_variant_id || null;
    }

    findBindingVariantWithOption(productId, optionName, optionValue) {
      const product = this.data.bundle_data.binding_products[productId];
      if (!product) return null;

      const currentVariantId = this.data.selection.binding_variant?.id;
      const currentVariant =
        this.data.bundle_data.binding_variants[currentVariantId];
      if (!currentVariant) return null;

      const newOptions = {
        ...currentVariant.options,
        [optionName]: optionValue,
      };

      const variantKey = this.buildVariantKey(newOptions);
      return product.variant_lookup[variantKey] || null;
    }

    findAvailableCramponVariant(productId) {
      const product = this.data.bundle_data.crampon_products[productId];
      if (!product) return null;

      const defaultVariant =
        this.data.bundle_data.crampon_variants[product.default_variant_id];
      if (defaultVariant?.available) {
        return product.default_variant_id;
      }

      for (const variantId of Object.values(product.variant_lookup)) {
        const variant = this.data.bundle_data.crampon_variants[variantId];
        if (variant?.available) {
          return variantId;
        }
      }

      return null;
    }

    hasCramponAvailableVariant(productId) {
      const product = this.data.bundle_data.crampon_products[productId];
      if (!product) return false;

      return Object.values(product.variant_lookup).some((variantId) => {
        const variant = this.data.bundle_data.crampon_variants[variantId];
        return variant?.available;
      });
    }

    onMountingVideoButtonClick = (event) => {
      let nextPlayState = false;

      this.mountingVideos.forEach((video) => {
        if (
          this.mountingVideoPlaying === true ||
          getComputedStyle(video).getPropertyValue("display") === "none"
        ) {
          video.pause();
        } else {
          nextPlayState = true;
          video.play();
        }
      });

      this.mountingVideoPlaying = nextPlayState;

      this.updateMountingVideoButton();
    };

    updateMountingVideoButton = () => {
      if (this.mountingVideoPlaying === true) {
        this.mountingVideoButton.classList.add("is-playing");
        this.mountingVideoButton.querySelector(".play__icon").style.display =
          "none";
        this.mountingVideoButton.querySelector(".pause__icon").style.display =
          "block";
      } else {
        this.mountingVideoButton.classList.remove("is-playing");
        this.mountingVideoButton.querySelector(".play__icon").style.display =
          "block";
        this.mountingVideoButton.querySelector(".pause__icon").style.display =
          "none";
      }
    };
  };

  if (!window.customElements.get("zag-featured-product")) {
    window.customElements.define(
      "zag-featured-product",
      window.zag.components.ZagFeaturedProduct
    );
  }

  window.zag.components.ZagSlideCounter = class extends (
    window.zag.components.ZagComponent
  ) {
    connectedCallback() {
      if (this.slider) {
        this.events.add(
          this.slider,
          "zag-slider:change",
          this.handleSlideChange
        );
      }
    }

    get slider() {
      return this.cache.get("dom", "slider", () => {
        const ariaControls = this.getAttribute("aria-controls");
        return ariaControls ? document.getElementById(ariaControls) : null;
      });
    }

    get currentSlideElement() {
      return this.cache.get("dom", "currentSlideElement", () =>
        this.querySelector("[data-current-slide]")
      );
    }

    handleSlideChange = (event) => {
      const { index } = event.detail;
      const currentSlideNumber = index + 1;

      if (this.currentSlideElement) {
        this.currentSlideElement.setAttribute(
          "data-current-slide",
          currentSlideNumber
        );
        this.currentSlideElement.textContent = currentSlideNumber;
      }
    };
  };

  if (!window.customElements.get("zag-slide-counter")) {
    window.customElements.define(
      "zag-slide-counter",
      window.zag.components.ZagSlideCounter
    );
  }
})();
